import { call, delay, put, select, takeLatest, takeEvery } from 'redux-saga/effects'
import { toast } from 'react-toastify'
import ActionsAPI from 'services/ActionsAPI'
import { getTaskStatsRoutine } from 'features/taskManagement/ducks/actions'
import {
  getActionsRoutine,
  refreshActionsRoutine,
  getActionStatsRoutine,
  getActionSectionRoutine,
  getActionRoutine,
  createActionRoutine,
  updateActionRoutine,
  convertActionToTaskRoutine,
  dismissActionRoutine,
  approveActionRoutine,
  reopenActionRoutine,
  bulkDismissActionsRoutine,
  bulkConvertActionsRoutine,
  bulkUpdateActionsRoutine,
  extractActionsFromFileRoutine,
  refreshAllActionStatsRoutine,
} from './actions'
import { bulkConvertMessage, bulkDismissMessage, bulkUpdateMessage, firstErrorMessage } from './messages'
import { toastWithAction, toastWithLink } from 'components/ActionToast'
import { PRIVATE_PATHS } from 'utils/paths'
import { paramsKey, listParamsKey } from './paramsKey'

export const UNDO_BULK_DISMISS = 'actions/UNDO_BULK_DISMISS'

const dispatchFromToast = action => window.reduxStore?.dispatch(action)

let lastRequestId = 0
const nextRequestId = () => {
  lastRequestId += 1
  return `remedial-${lastRequestId}`
}
const withMeta = (action, meta) => ({ ...action, meta: { ...(action.meta || {}), ...meta } })

function* undoBulkDismiss({ payload }) {
  const ids = payload?.ids || []
  const requestId = nextRequestId()
  yield put(withMeta(reopenActionRoutine.request(), { requestId }))
  const results = yield call(() => Promise.allSettled(ids.map(id => ActionsAPI.reopen(id))))
  const restored = results.filter(r => r.status === 'fulfilled').length
  yield put(withMeta(reopenActionRoutine.success({}), { requestId }))
  yield put(refreshAllActionStatsRoutine())
  if (restored === ids.length) toast.success(`${restored} remedial${restored === 1 ? '' : 's'} restored.`)
  else toast.error(`Restored ${restored} of ${ids.length} remedials. Please try the rest again.`)
}

export const handle = (routine, fn) => function* (action) {
  const requestId = nextRequestId()
  try {
    // Forward the trigger payload to REQUEST so the reducer can read it
    // (e.g. clear the list immediately on page-1 list fetches to avoid
    // showing stale rows while the new filter result is in flight).
    yield put(withMeta(routine.request(action.payload), { requestId }))
    const response = yield call(fn, action.payload)
    yield put(withMeta(routine.success(response?.data), { requestId }))
  } catch (e) {
    yield put(withMeta(routine.failure(e?.response?.data || { message: e.message }), { requestId }))
  } finally {
    yield put(withMeta(routine.fulfill(), { requestId }))
  }
}

export function* fetchStats({ payload }) {
  const params = payload || {}
  const statsKey = paramsKey(params)
  const requestId = nextRequestId()
  try {
    yield put(withMeta(getActionStatsRoutine.request(params), { statsKey, requestId }))
    const response = yield call(ActionsAPI.stats, params)
    yield put(withMeta(getActionStatsRoutine.success(response?.data), { statsKey, requestId }))
  } catch (e) {
    yield put(withMeta(getActionStatsRoutine.failure(e?.response?.data || { message: e.message }), { statsKey, requestId }))
  } finally {
    yield put(getActionStatsRoutine.fulfill())
  }
}

export function* refreshAllStats() {
  const cached = yield select(state => state.actions?.statsParamsByKey || {})
  const all = Object.values(cached)
  const targets = all.length ? all : [{}]
  for (const params of targets) {
    yield put(getActionStatsRoutine(params))
  }
}

function* fetchSection({ payload }) {
  const key = payload?.key
  const requestKey = paramsKey(payload?.params)
  try {
    yield put(getActionSectionRoutine.request({ key, requestKey }))
    const response = yield call(ActionsAPI.list, payload?.params || {})
    yield put(getActionSectionRoutine.success({ key, requestKey, items: response?.data?.data ?? [], total: response?.data?.meta?.total ?? 0 }))
  } catch (e) {
    yield put(getActionSectionRoutine.failure({ key, requestKey }))
  } finally {
    yield put(getActionSectionRoutine.fulfill({ key }))
  }
}

function* fetchList(routine, action) {
  const payload = action?.payload
  const requestKey = listParamsKey(payload)
  try {
    yield put(routine.request(payload))
    const response = yield call(ActionsAPI.list, payload || {})
    yield put(routine.success({ ...response?.data, requestKey }))
  } catch (e) {
    yield put(routine.failure(e?.response?.data || { message: e.message }))
  } finally {
    yield put(routine.fulfill())
  }
}

function* bulkUpdate({ payload }) {
  const requested = payload?.ids?.length ?? 0
  const requestId = nextRequestId()
  try {
    yield put(withMeta(bulkUpdateActionsRoutine.request(payload), { requestId }))
    const response = yield call(ActionsAPI.bulkUpdate, payload)
    const result = response?.data?.data ?? {}
    yield put(withMeta(bulkUpdateActionsRoutine.success(result), { requestId }))
    const message = bulkUpdateMessage(result, requested)
    toast[message.tone](message.text)
  } catch (e) {
    yield put(withMeta(bulkUpdateActionsRoutine.failure(e?.response?.data || { message: e.message }), { requestId }))
    toast.error(firstErrorMessage(e?.response?.data, 'Could not update the selected remedials. Please try again.'))
  } finally {
    yield put(withMeta(bulkUpdateActionsRoutine.fulfill(), { requestId }))
  }
}

export default function* actionsSagas() {
  yield takeLatest(getActionsRoutine.TRIGGER, fetchList, getActionsRoutine)
  yield takeLatest(refreshActionsRoutine.TRIGGER, fetchList, refreshActionsRoutine)
  yield takeEvery(getActionSectionRoutine.TRIGGER, fetchSection)
  yield takeEvery(bulkUpdateActionsRoutine.TRIGGER, bulkUpdate)
  yield takeEvery(updateActionRoutine.SUCCESS, () => {
    toast.success('Changes saved.')
  })
  yield takeEvery(updateActionRoutine.FAILURE, ({ payload }) => {
    toast.error(firstErrorMessage(payload, 'Could not save the change. Please try again.'))
  })
  yield takeEvery(getActionStatsRoutine.TRIGGER, fetchStats)
  yield takeLatest(refreshAllActionStatsRoutine.TRIGGER, refreshAllStats)
  yield takeLatest(getActionRoutine.TRIGGER, handle(getActionRoutine, id => ActionsAPI.get(id)))
  yield takeEvery(createActionRoutine.TRIGGER, handle(createActionRoutine, p => ActionsAPI.create(p)))
  yield takeEvery(updateActionRoutine.TRIGGER, handle(updateActionRoutine, ({ id, ...rest }) => ActionsAPI.update(id, rest)))
  yield takeEvery(convertActionToTaskRoutine.TRIGGER, handle(convertActionToTaskRoutine, ({ id, ...rest }) => ActionsAPI.convertToTask(id, rest)))
  yield takeEvery(dismissActionRoutine.TRIGGER, handle(dismissActionRoutine, ({ id, ...rest }) => ActionsAPI.dismiss(id, rest)))
  yield takeEvery(approveActionRoutine.TRIGGER, handle(approveActionRoutine, id => ActionsAPI.approve(id)))
  yield takeEvery(reopenActionRoutine.TRIGGER, handle(reopenActionRoutine, id => ActionsAPI.reopen(id)))
  yield takeEvery(bulkDismissActionsRoutine.TRIGGER, handle(bulkDismissActionsRoutine, p => ActionsAPI.bulkDismiss(p)))
  yield takeEvery(bulkConvertActionsRoutine.TRIGGER, handle(bulkConvertActionsRoutine, p => ActionsAPI.bulkConvert(p)))

  // Cross-feature consequences. A successful convert (single or bulk) created
  // one or more Tasks — refresh the Task stats so the hub badge bumps without
  // requiring a navigation. Same idea for approve (a pending-review action
  // becoming open should bump the Actions badge, but the same-page refresh
  // already handles that; this is the cross-domain case).
  yield takeEvery(convertActionToTaskRoutine.SUCCESS, function* ({ payload }) {
    const remedial = payload?.data || {}
    const tasks = Array.isArray(remedial.linkedTasks) ? remedial.linkedTasks : []
    const taskId = remedial.taskId || tasks[0]?.id
    if (tasks.length > 1) {
      toastWithLink('success', `${tasks.length} tasks created from this remedial.`, 'View tasks', `${PRIVATE_PATHS.taskManagement}?tab=tasks&origin=corrective`)
    } else if (taskId) {
      toastWithLink('success', 'Task created from this remedial.', 'Open task', PRIVATE_PATHS.taskDetails.replace(':id', taskId))
    } else {
      toast.success('Task created from this remedial.')
    }
    yield put(getTaskStatsRoutine())
  })
  yield takeEvery(createActionRoutine.SUCCESS, ({ payload }) => {
    const created = payload?.data
    if (!created?.id) return
    const path = PRIVATE_PATHS.actionDetail.replace(':id', created.id)
    if (payload?.alreadyExists) toastWithLink('info', 'This remedial already exists on the building.', 'Open', path)
    else toastWithLink('success', 'Remedial added.', 'Open', path)
  })
  yield takeEvery(createActionRoutine.FAILURE, ({ payload }) => {
    toast.error(firstErrorMessage(payload, 'Could not add the remedial. Please try again.'))
  })
  yield takeEvery(dismissActionRoutine.SUCCESS, ({ payload }) => {
    const id = payload?.data?.id
    if (!id) return
    toastWithAction('info', 'Removed as not a real issue.', 'Undo', () => dispatchFromToast(reopenActionRoutine(id)))
  })
  yield takeEvery(dismissActionRoutine.FAILURE, ({ payload }) => {
    toast.error(firstErrorMessage(payload, 'This AI find could not be removed. Please try again.'))
  })
  yield takeEvery(reopenActionRoutine.SUCCESS, ({ payload }) => {
    if (payload?.data?.id) toast.success('Remedial reopened.')
  })
  yield takeEvery(reopenActionRoutine.FAILURE, ({ payload }) => {
    toast.error(firstErrorMessage(payload, 'Could not reopen the remedial. Please try again.'))
  })
  yield takeEvery(UNDO_BULK_DISMISS, undoBulkDismiss)
  yield takeEvery(convertActionToTaskRoutine.FAILURE, () => {
    toast.error('Could not create the task from this remedial. Please try again.')
  })
  yield takeEvery(bulkConvertActionsRoutine.SUCCESS, function* ({ payload }) {
    const message = bulkConvertMessage(payload?.data)
    const converted = Array.isArray(payload?.data?.convertedIds) ? payload.data.convertedIds.length : Number(payload?.data?.convertedCount ?? 0)
    if (converted > 0) toastWithLink(message.tone, message.text, 'View tasks', `${PRIVATE_PATHS.taskManagement}?tab=tasks&origin=corrective`)
    else toast[message.tone](message.text)
    yield put(getTaskStatsRoutine())
  })
  yield takeEvery(bulkConvertActionsRoutine.FAILURE, ({ payload }) => {
    toast.error(firstErrorMessage(payload, 'Could not create tasks from the selected remedials. Please try again.'))
  })
  yield takeEvery(bulkDismissActionsRoutine.SUCCESS, ({ payload }) => {
    const message = bulkDismissMessage(payload?.data)
    const ids = Array.isArray(payload?.data?.dismissedIds) ? payload.data.dismissedIds : []
    if (ids.length) toastWithAction(message.tone, message.text, 'Undo', () => dispatchFromToast({ type: UNDO_BULK_DISMISS, payload: { ids } }))
    else toast[message.tone](message.text)
  })
  yield takeEvery(bulkDismissActionsRoutine.FAILURE, ({ payload }) => {
    toast.error(firstErrorMessage(payload, 'The selected AI finds could not be removed. Please try again.'))
  })
  yield takeLatest(extractActionsFromFileRoutine.TRIGGER, function* (action) {
    const fileId = action.payload
    try {
      yield put(extractActionsFromFileRoutine.request(fileId))
      const queued = yield call(() => ActionsAPI.extractFromFile(fileId))
      yield put(extractActionsFromFileRoutine.success({ fileId }))
      toast.info('Guru is reading your document. We\u2019ll let you know when remedials land.')

      const baselineExtractedAt = queued?.data?.extractedAt ?? null
      const before = queued?.data?.remedialsCount ?? 0

      const start = Date.now()
      let done = false
      while (Date.now() - start < 90_000) {
        yield delay(3000)
        try {
          const res = yield call(() => ActionsAPI.extractionStatus(fileId))
          const extractedAt = res?.data?.extractedAt ?? null
          const count = res?.data?.remedialsCount ?? before
          if (extractedAt && extractedAt !== baselineExtractedAt) {
            const added = count - before
            if (added > 0) {
              toast.success(`Guru extracted ${added} new remedial${added === 1 ? '' : 's'} from your document.`)
            } else {
              toast.info('Guru has finished. It didn’t find any new remedials to add. Anything it spotted already exists and is not duplicated.')
            }
            done = true
            break
          }
        } catch { /* keep polling */ }
      }
      if (!done) {
        toast.info('Guru is still working on this document. Any new remedials will appear shortly.')
      }
    } catch (e) {
      yield put(extractActionsFromFileRoutine.failure(e?.response?.data || { message: e.message }))
      toast.error(e?.response?.data?.message || 'Couldn\u2019t queue extraction. Please try again.')
    } finally {
      yield put(extractActionsFromFileRoutine.fulfill())
    }
  })
}

